import { resolveSource, sourceLabel } from "../services/leadSources";
import { LEAD_PRODUCTS } from "../services/leadsService";

// ===========================================================================
// Filtros en desplegable de las listas de Contactos y Leads (Bandeja,
// Contactos y Empresas). Las tres pantallas filtran igual: un <select> por
// criterio, con «Todos» arriba y el número de registros de cada opción.
// ===========================================================================

/** Valor del desplegable cuando no se filtra por ese criterio. */
export const FILTER_ALL = "all";
/** Valor de la opción «Sin producto» / «Sin origen» (registros sin el dato). */
export const FILTER_NONE = "__none__";

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export function FilterSelect({
  label,
  value,
  onChange,
  options,
  allLabel = "Todos",
  allCount,
  isDarkMode,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  allLabel?: string;
  allCount?: number;
  isDarkMode: boolean;
}) {
  const withCount = (text: string, count?: number) => (count == null ? text : `${text} (${count})`);
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className={`font-medium uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
        {label}
      </span>
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`rounded-lg border px-2 py-1.5 text-sm outline-none ${
          value !== FILTER_ALL
            ? "border-primary-500"
            : isDarkMode ? "border-dark-border" : "border-gray-300"
        } ${isDarkMode ? "bg-[#232323] text-white" : "bg-white text-gray-900"}`}
      >
        <option value={FILTER_ALL}>{withCount(allLabel, allCount)}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{withCount(o.label, o.count)}</option>
        ))}
      </select>
    </label>
  );
}

/** Llave de filtro del producto de un registro (`FILTER_NONE` si no tiene). */
export function productFilterKey(product: string | null | undefined): string {
  return (product ?? "").trim() || FILTER_NONE;
}

/**
 * Llave de filtro del origen: la fuente canónica cuando se reconoce (así
 * "landing" y "coding-cloud-landing" caen en la misma opción), el valor crudo
 * cuando no, y `FILTER_NONE` si el registro no trae origen.
 */
export function sourceFilterKey(source: string | null | undefined): string {
  const raw = (source ?? "").trim();
  if (!raw) return FILTER_NONE;
  const def = resolveSource(raw);
  return def.key === "otro" ? raw.toLowerCase() : def.key;
}

/** ¿El registro pasa el filtro? (`FILTER_ALL` deja pasar todo). */
export function matchesFilter(filter: string, key: string): boolean {
  return filter === FILTER_ALL || filter === key;
}

function countBy<T>(rows: T[], keyOf: (row: T) => string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const k = keyOf(r);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return counts;
}

/**
 * Opciones de «Producto»: los productos del catálogo siempre, más cualquier
 * otro valor presente en los datos y «Sin producto» si hay registros sin él.
 */
export function productFilterOptions<T extends { product?: string | null }>(rows: T[]): FilterOption[] {
  const counts = countBy(rows, (r) => productFilterKey(r.product));
  const known: string[] = [...LEAD_PRODUCTS];
  const extra = Array.from(counts.keys())
    .filter((k) => k !== FILTER_NONE && !known.includes(k))
    .sort((a, b) => a.localeCompare(b, "es"));
  const options: FilterOption[] = [...known, ...extra].map((p) => ({ value: p, label: p, count: counts.get(p) ?? 0 }));
  if (counts.has(FILTER_NONE)) options.push({ value: FILTER_NONE, label: "Sin producto", count: counts.get(FILTER_NONE) });
  return options;
}

/** Opciones de «Origen»: los orígenes presentes en los datos, con su etiqueta. */
export function sourceFilterOptions<T extends { source?: string | null }>(rows: T[]): FilterOption[] {
  const counts = countBy(rows, (r) => sourceFilterKey(r.source));
  const labels = new Map<string, string>();
  for (const r of rows) {
    const k = sourceFilterKey(r.source);
    if (k !== FILTER_NONE && !labels.has(k)) labels.set(k, sourceLabel(r.source));
  }
  const options: FilterOption[] = Array.from(labels.entries())
    .map(([value, label]) => ({ value, label, count: counts.get(value) }))
    .sort((a, b) => a.label.localeCompare(b.label, "es"));
  if (counts.has(FILTER_NONE)) options.push({ value: FILTER_NONE, label: "Sin origen", count: counts.get(FILTER_NONE) });
  return options;
}

/** Opciones de «Etapa» / «Estado» a partir de un orden y sus etiquetas. */
export function stageFilterOptions<S extends string>(
  order: S[],
  meta: Record<string, { label: string }>,
  counts: Map<string, number> | Record<string, number>,
): FilterOption[] {
  const get = (k: string) => (counts instanceof Map ? counts.get(k) : counts[k]) ?? 0;
  return order.map((s) => ({ value: s, label: meta[s]?.label ?? s, count: get(s) }));
}

export { countBy };
